// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { features } from '@/lib/edition';

export const BRAND_NAME = 'Hanggent';

export const DEFAULT_SITE_URL = 'https://www.hangent.com';

/**
 * Replace the upstream product name in user-facing text. Only the capitalised
 * whole word changes, so hosts and protocols (eigent.ai, eigent://) survive.
 */
export function applyBrand(text: string): string {
  return text.replace(/\bEigent\b/g, BRAND_NAME);
}

/**
 * Rebrand every string in a resource tree once, at load. Done on the
 * templates rather than at translation time so interpolated values (paths,
 * names, server errors) are never rewritten.
 */
export function brandResources<T>(tree: T): T {
  if (typeof tree === 'string') return applyBrand(tree) as T;
  if (Array.isArray(tree)) return tree.map((v) => brandResources(v)) as T;
  if (tree !== null && typeof tree === 'object') {
    return Object.fromEntries(
      Object.entries(tree).map(([k, v]) => [k, brandResources(v)])
    ) as T;
  }
  return tree;
}

/**
 * The web app links back to its own origin; the desktop app (file or custom
 * protocol) to the public site. Reads the page location directly because this
 * runs while @/lib loads.
 */
export function defaultSiteUrl(): string {
  // Only the cloud web build serves /pricing, /dashboard and friends itself.
  if (
    features.billing &&
    typeof window !== 'undefined' &&
    /^https?:/.test(window.location?.protocol ?? '')
  ) {
    return window.location.origin;
  }
  return DEFAULT_SITE_URL;
}
